/**
 * Wire protocol: message names + payload shapes shared by client and server.
 * The server is authoritative — clients only ever send *inputs*, never results.
 */
/** Messages the client sends to the game server. */
export const ClientMsg = {
    Input: "input",
    /** LOAD READY: the client's match load screen has closed (map built, shaders
     *  compiled, models warmed) and the player can actually play. No payload.
     *  Only honoured for sessions that joined with `JOIN_LOAD_READY_OPT: true`
     *  (older clients / headless harnesses never send it and keep the legacy
     *  "live from join" behaviour). Until it arrives (or LOAD_READY_CAP_MS
     *  passes) the server keeps that player invulnerable and off every bot's
     *  target list; a round whose only participants are still loading is held
     *  frozen, then released with a ServerMsg.RoundCountdown. */
    Ready: "ready",
    Chat: "chat",
    /** Lobby (owner only unless noted): pick map, set bot count, start the game. */
    LobbyMap: "lobby_map",
    LobbyMode: "lobby_mode",
    LobbyBots: "lobby_bots",
    LobbyStart: "lobby_start",
    /** Any pending player: drop into an in-progress game (parachute). */
    LobbyJoinLive: "lobby_join_live",
    /** Owner: skip the end-of-match countdown — back to the lobby right away
     *  (settings preserved) so the next round can start immediately. */
    SkipOver: "skip_over",
    /** Latency probe — server echoes the payload back as Pong. */
    Ping: "ping",
    /** Client reports its measured round-trip time (ms) so the server can rewind
     *  lag-compensated hitreg by THIS shooter's real latency instead of a fixed
     *  slack. Sent ~0.5 Hz off the Ping/Pong clock. Payload: { rtt: number }. */
    NetStat: "netstat",
    /** Owner (teams mode): assign a player/bot to team 1 or 2. */
    LobbyTeam: "lobby_team",
    /** Step out of the running match back to the room lobby (rejoin later). */
    Retreat: "retreat",
    /** Owner: enable/disable vehicles for this room. */
    LobbyVehicles: "lobby_vehicles",
    /** Owner: enable/disable TEAMS mode for this room. */
    LobbyTeamsOn: "lobby_teams",
    /** Owner: set the round time in seconds (0 = no timer). */
    LobbyTimer: "lobby_timer",
    /** Owner toggles whether ANYONE may pause the running game. */
    LobbyPause: "lobby_pause",
    /** Pause/resume the whole match (solo always; rooms when pauseAllowed). */
    Pause: "pause",
    /** Owner: toggle special map SFX (giant monsters). */
    LobbyMonsters: "lobby_monsters",
    /** Owner: toggle the day/night cycle for this room. */
    LobbyNightCycle: "lobby_nightcycle",
    /** Owner: set the ROUND MODIFIER for this room ("" = standard rules). */
    LobbyModifier: "lobby_modifier",
    /** ANY player (not just the owner) sets their own LOADOUT perk ("" = none).
     *  Per-player, unlike every Lobby* message above — hence no `Lobby` prefix. */
    Loadout: "loadout",
    /** EMOTE WHEEL: the player CHOSE a taunt from the radial wheel ({kind:0..4}).
     *  Unlike the Buttons.Taunt bit (which the server randomizes), this carries
     *  the exact emote the player selected, so the wheel picks what everyone
     *  sees/hears. Per-player, not owner-gated. */
    Emote: "emote",
    /** Easter egg: the player gibbed an ambient animal (client-detected, the
     *  server rolls a rate-limited bonus). */
    CritterKill: "critter_kill",
    /** PURGE team play: one revive tap (mash Use near a fallen teammate). */
    Revive: "purge_revive",
    /** PURGE roguelite: pick one of the offered end-of-round perks ({id}). */
    PurgePerk: "purge_perk",
    /** PURGE shop: buy a PURGE_SHOP item ({id}) — must be near a buy station. */
    ShopBuy: "shop_buy",
    /** Owner: set AI bot difficulty (easy | medium | hard). */
    LobbyDifficulty: "lobby_difficulty",
    /** Player: report aim-assist on/off (drives the "?" marker). */
    SetAutoAim: "set_auto_aim",
    /** Owner: force aim assist OFF for the whole room. */
    LobbyNoAutoAim: "lobby_no_autoaim",
    /** Owner: GUN GAME option — power-up / vehicle kills demote the victim ({on}). */
    LobbyPickupDemote: "lobby_pickup_demote",
    /** VOICE CHAT: WebRTC signaling relayed to one other player ({to, data}). */
    VoiceSignal: "voice_signal",
    /** BETWEEN-ROUND MAP VOTE: ANY player casts/changes their vote for the next
     *  map during the end-of-round "over" screen ({ index } into the candidate
     *  list the server published in `rules.vote.options`). Not owner-gated — the
     *  whole point is that non-owners get a say in the next map. */
    VoteMap: "vote_map",
    /** Owner: one-click REMATCH from the end-of-match "over" screen — restart the
     *  next round IMMEDIATELY (on the map the between-round vote picked) without
     *  bouncing everyone through the room lobby. The auto-return-to-lobby timer
     *  still fires if the owner does nothing, so setup changes stay reachable. */
    Rematch: "rematch",
};
/** Messages the game server broadcasts to clients (outside schema state sync). */
export const ServerMsg = {
    Snapshot: "snapshot",
    /** Treasure holder's banked second (1..30), for the spoken count-up. */
    TreasureCount: "treasure_count",
    /** Echo of ClientMsg.Ping (same payload) — client computes RTT. */
    Pong: "pong",
    Kill: "kill",
    /** Sent to the attacker only — drives the hitmarker. */
    Hit: "hit",
    /** Sent to the victim only — drives the directional damage indicator. */
    Damaged: "damaged",
    /** Broadcast (except the shooter) so remote clients render tracers/flash/SFX. */
    Shot: "shot",
    Promoted: "promoted",
    Demoted: "demoted",
    MatchOver: "match_over",
    /** Multi-kill / killstreak announcement (to the achiever). */
    Announce: "announce",
    /** FART BLASTER: someone fired a smoke ring — {x,y,z,dx,dy,dz,n}
     *  (n = which recorded fart, 1..FARTBLASTER.sounds; dir = the full 3D aim).
     *  Clients re-derive the whole flight from this one event. */
    FartFire: "fart_fire",
    /** A ring dissipated at {x,y,z} (range end or wall) — green puff. */
    FartPop: "fart_pop",
    /** {sid} got smoked: everyone plays throwup at them; the victim's client
     *  applies the 2 s green haze overlay. */
    FartHit: "fart_hit",
    /** PENIS GUN: {sid} is being hosed by the sperm jet — the victim's client
     *  applies an underwater blur overlay. Re-sent every PENISGUN.hitRefreshMs
     *  while they stay in the stream so the overlay holds. */
    SpermHit: "sperm_hit",
    /** WATER GUN: {sid,intensity} is being hosed by water — victim gets an
     *  underwater blur that builds while the stream stays on target. */
    WaterHit: "water_hit",
    /** Vehicle destroyed — spawn an explosion at x/y/z. */
    Explosion: "explosion",
    /** EASTER EGG: 10-animal DUBSTEP ARMAGEDDON — client rains sky-lasers for the
     *  music duration; server rains lethal strikes (kill + demote everyone). */
    Armageddon: "armageddon",
    /** A single sky-laser strike touchdown (x/z) during the armageddon. */
    SkyStrike: "sky_strike",
    /** THE RING (battle-royale compression pulse): re-sent on a cadence while the
     *  pulse is live so a mid-pulse joiner catches up. `{ x, z, radius }` = the
     *  current safe-zone; `{ active:false }` clears it. Kept OFF replicated state
     *  because GunGameState is at the 64-field schema cap. */
    Ring: "ring",
    /** STORM lightning: a bolt is charging at (x,z); client telegraphs then flashes
     *  ~0.85s later. Server strikes (damage) on the same beat. */
    Lightning: "lightning",
    /** Green music chest collected — everyone switches to the chosen track. */
    MusicChange: "music_change",
    /** A driver was booted from their vehicle (positional 'goaway' cue). */
    VehicleSteal: "vehicle_steal",
    /** A pickup was collected (drives sound + heal flash). */
    Picked: "picked",
    /** Relayed text chat. */
    Chat: "chat",
    /** A player dropped into an in-progress game (drives the "new player" banner). */
    DropIn: "drop_in",
    /** UFO/mini-UFO exit launch — render a violet tractor beam. */
    EjectBeam: "eject_beam",
    /** Practice-Field dragon spat a beam: render ox/oy/oz → tx/ty/tz. */
    DragonBeam: "dragon_beam",
    /** Wild-West sandworm spat acid: render an arcing green splash. */
    WormAcid: "worm_acid",
    /** PURGE roguelite: choose 1 of 3 perks ({options: [{id,icon,name,desc}]}).
     *  Answer with ClientMsg.PurgePerk before the next round starts. */
    PerkOffer: "perk_offer",
    /** PURGE shop: purchase outcome ({ok, id, reason?}) — sent to the buyer. */
    ShopResult: "shop_result",
    /** DeLorean time jump: everyone plays the cinematic before the room swaps map. */
    TimeJump: "time_jump",
    /** DeLorean arrival: the new map is live; play the low-pitched beam arrival. */
    TimeJumpArrive: "time_jump_arrive",
    /** VOICE CHAT: WebRTC signaling from another player ({from, data}). */
    VoiceSignal: "voice_signal",
    /** Progression: XP banked for a finished match ({xp, total?}) — drives the
     *  level-up card. `total` is lifetime XP AFTER banking (omitted if the DB seed
     *  hadn't loaded yet, in which case the client shows a plain "+N XP" card). */
    XpAward: "xp_award",
    /** PRACTICE LADDER SPRINT: the kill that left tier 0 armed the run clock —
     *  sent to the sprinter only (drives the HUD timer; the server keeps the
     *  authoritative clock). */
    SprintStart: "sprint_start",
    /** PRACTICE LADDER SPRINT: full ladder completed ({ms}) — sent to the
     *  sprinter only. Records are client-local (practice is private). */
    SprintDone: "sprint_done",
    /** LOAD READY: the frozen round-start hold ended (the first participant
     *  finished loading, or the cap hit). `{ ms }` until bots wake up and the
     *  round clock starts — the client draws a 3-2-1-GO from receipt. Sent only
     *  to the in-match human clients, never the lobby/spectators. */
    RoundCountdown: "round_countdown",
};
/** Join option a client sets to declare it will send ClientMsg.Ready when its
 *  load screen closes. Absent → the server never waits on that session. */
export const JOIN_LOAD_READY_OPT = "loadReady";
/** Longest the server waits for a session's ClientMsg.Ready (per player, and
 *  for a held round start) before treating it as ready anyway. A slow or hung
 *  client can never freeze a round or stay invulnerable longer than this. */
export const LOAD_READY_CAP_MS = 20_000;
/** The visible 3-2-1-GO after a held round start is released. */
export const ROUND_COUNTDOWN_MS = 3_000;
/** Button bitmask for PlayerInput.buttons. */
export const Buttons = {
    Fire: 1 << 0,
    ADS: 1 << 1,
    Jump: 1 << 2,
    Crouch: 1 << 3,
    Sprint: 1 << 4,
    Reload: 1 << 5,
    Knife: 1 << 6,
    Use: 1 << 7,
    Grenade: 1 << 8,
    Glide: 1 << 9,
    /** Vehicle free-look (Shift / LB held while driving). */
    FreeLook: 1 << 10,
    /** Use one stored health shot (D-pad up / ArrowUp). */
    Heal: 1 << 11,
    /** Play a random taunt animation (D-pad down / ArrowDown). */
    Taunt: 1 << 12,
    /** GRAPPLING HOOK: hold to fire a line at what you're looking at and reel in. */
    Grapple: 1 << 13,
    /** BURNOUT — STOKE: voluntarily light your own fuse below the top rung
     *  (kill inside the window = bonus rungs; expiry = burn out). Edge-triggered
     *  server-side; inert in every other mode. */
    Stoke: 1 << 14,
};
// ------------------------------------------------------------ grapple anchor
// The grappling hook is predicted locally but was never replicated, so a
// remote player (or bot) reeling through the air rendered ropeless — pure
// flight, illegible. PlayerState is one field short of Colyseus's 64-field
// schema cap, so the whole anchor rides in ONE float64: 0 = detached, else
// 1 + three axes quantized to 16 bits at 1/32 m around ±1024 m. Measured
// world extents across every map are ±420 m, and ~3 cm of error on a rope
// endpoint is invisible. The +1 keeps a legal all-zero-bits anchor distinct
// from "detached". Max packed value ≈ 2^48, exact in a float64.
const GRAP_SCALE = 32;
const GRAP_OFFSET = 1024;
function grapQ(v) {
    const q = Math.round((v + GRAP_OFFSET) * GRAP_SCALE);
    return q < 0 ? 0 : q > 0xffff ? 0xffff : q;
}
export function packGrapAnchor(x, y, z) {
    return 1 + grapQ(x) + grapQ(y) * 0x10000 + grapQ(z) * 0x100000000;
}
/** Decodes a packed anchor into `out` (no allocation — runs per remote per
 *  frame). Returns false when the value reads "detached". */
export function unpackGrapAnchor(v, out) {
    if (!Number.isFinite(v) || v < 1)
        return false;
    const p = v - 1;
    out.x = (p % 0x10000) / GRAP_SCALE - GRAP_OFFSET;
    out.y = (Math.floor(p / 0x10000) % 0x10000) / GRAP_SCALE - GRAP_OFFSET;
    out.z = Math.floor(p / 0x100000000) / GRAP_SCALE - GRAP_OFFSET;
    return true;
}
//# sourceMappingURL=protocol.js.map